import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MusicOffIcon from "@mui/icons-material/MusicOff";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, Navigate, useParams, useSearchParams } from "react-router-dom";
import Footer from "@/components/footer";
import Layout from "@/components/layout";
import SEO from "@/components/seo";
import { ArtistNav } from "@/components/templates/ArtistTemplate/ArtistNav";
import ArcadeSongCard, { badgeFor } from "@/components/arcade/ArcadeSongCard";
import ArcadeStage from "@/components/arcade/ArcadeStage";
import GameThumbnail from "@/components/arcade/GameThumbnail";
import SongLinksSheet from "@/components/songLinks/SongLinksSheet";
import { ARCADE_ARTIST, useArcadeLineup } from "@/components/arcade/useArcadeLineup";
import { sameSong, useArcadeTrack } from "@/components/arcade/useArcadeTrack";
import { useSongTheme } from "@/components/arcade/useSongTheme";
import { ChallengeCountdown } from "@/components/arcade/retention/DailyChallengeCard";
import { useArcadeRetention } from "@/components/arcade/retention/useArcadeRetention";
import { CHALLENGE_PARAM, dailyChallengeFor, isOpenChallengeDate } from "@/utils/arcade/dailyChallenge";
import { arcadeThemeFor } from "@/utils/arcade/theme";
import { getArtistPath, getOptimizedImageUrl, safeStringWithHyphens, trackClick } from "@/components/_helpers";
import { usePlaylist } from "@/contexts/PlaylistContext";
import { ARCADE_DIFFICULTIES, ARCADE_GAME_KINDS, ARCADE_GAMES, DIFFICULTY_LABELS, gameDescription, gameTitle, isArcadeGameKind, type ArcadeDifficulty, type ArcadeGameKind } from "@/utils/arcade/games";
import { arcadePath, findArcadeSong, releaseYear } from "@/utils/arcade/lineup";
import { readArcadePrefs, writeArcadePrefs, type ArcadePrefs } from "@/utils/arcade/scores";
import { PALETTE_CHOICES, PALETTE_LABELS, type PaletteChoice } from "@/utils/arcade/theme";
import { RELEASE_KIND_LABELS } from "@/utils/musicReleaseTabs";
import { heroProps } from "@/utils/sharedElements";
import { useHoldForArrival, useSharedArrival } from "@/hooks/useSharedArrival";

const MORE_SONGS = 5;

const STAGE_HEIGHT = { xs: "min(76vh, 160vw)", md: "min(68vh, 660px)" };

const pickerRowSx = {
  display: "grid",
  gridAutoFlow: { xs: "column", md: "row" },
  gridAutoColumns: { xs: "132px", md: "unset" },
  gap: { xs: "10px", md: "12px" },
  overflowX: { xs: "auto", md: "visible" },
  scrollSnapType: "x mandatory",
  mx: { xs: "-2.5rem", md: 0 },
  px: { xs: "2.5rem", md: 0 },
  pb: "6px",
  mb: { xs: "12px", md: "18px" },
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
} as const;

function GameHeader({ artwork, title, eyebrow, eyebrowColor, glow, releasePath }: { artwork?: string; title: string; eyebrow: string; eyebrowColor?: string; glow?: string; releasePath?: string }) {
  return (
    <Box
      component="header"
      sx={{ display: "flex", alignItems: "center", gap: { xs: "16px", md: "22px" }, mt: "10px", mb: { xs: "20px", md: "26px" }, flexWrap: "wrap" }}
    >
      {artwork ? (
        <Box
          component="img"
          {...heroProps("art")}
          src={getOptimizedImageUrl(artwork, { width: 200, height: 200, fit: "cover", quality: 80 })}
          alt={`${title} cover art`}
          sx={{ width: { xs: "84px", md: "96px" }, height: { xs: "84px", md: "96px" }, borderRadius: "14px", objectFit: "cover", boxShadow: glow ? `0 16px 40px -18px ${glow}` : "none" }}
        />
      ) : null}
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Typography component="p" sx={{ ...labelSx, color: eyebrowColor ?? labelSx.color, opacity: eyebrowColor ? 1 : labelSx.opacity }}>
          {eyebrow}
        </Typography>
        <Typography component="h1" sx={{ m: "4px 0 0", fontFamily: "Hanson", fontWeight: 700, color: "var(--logoColor2)", fontSize: { xs: "3.5rem", md: "3rem" }, lineHeight: 1.05, overflowWrap: "anywhere" }}>
          <Box component="span" {...heroProps("title")} sx={{ display: "inline-block", maxWidth: "100%" }}>
            {title}
          </Box>
        </Typography>
        {releasePath ? (
          <Box
            component={RouterLink}
            to={releasePath}
            viewTransition
            sx={{ display: "inline-block", mt: "6px", color: "var(--logoColor)", fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, textDecorationColor: "color-mix(in srgb, var(--logoColor) 40%, transparent)", "&:hover": { color: "var(--logoColor2)" } }}
          >
            Listen on the release page
          </Box>
        ) : (
          <Box sx={{ mt: "6px", fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, lineHeight: 1.5 }}>&nbsp;</Box>
        )}
      </Box>
    </Box>
  );
}

const labelSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--logoColor)",
  opacity: 0.8,
} as const;

function Segmented<T extends string>({
  label,
  options,
  value,
  labels,
  onChange,
  accent,
  contrastText,
}: {
  label: string;
  options: readonly T[];
  value: T;
  labels: Record<T, string>;
  onChange: (value: T) => void;
  accent: string;
  contrastText: string;
}) {
  return (
    <Box role="group" aria-label={label} sx={{ display: "grid", gap: "8px" }}>
      <Typography component="span" sx={labelSx}>{label}</Typography>
      <Box sx={{ display: "inline-flex", p: "4px", gap: "4px", borderRadius: "999px", backgroundColor: "color-mix(in srgb, var(--color5) 80%, transparent)", border: "1px solid color-mix(in srgb, var(--logoColor) 18%, transparent)", width: "fit-content", maxWidth: "100%" }}>
        {options.map((option) => {
          const selected = option === value;
          return (
            <ButtonBase
              key={option}
              onClick={() => onChange(option)}
              aria-pressed={selected}
              sx={{
                minHeight: "40px",
                px: { xs: "14px", md: "16px" },
                borderRadius: "999px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: selected ? contrastText : "var(--logoColor2)",
                backgroundColor: selected ? accent : "transparent",
                transition: "background-color 0.2s ease, color 0.2s ease",
                "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: "2px" },
              }}
            >
              {labels[option]}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

export default function GamePage() {
  const { songSlug, gameKind } = useParams<{ songSlug: string; gameKind?: string }>();
  const [searchParams] = useSearchParams();
  const { artist, lineup, loading } = useArcadeLineup();
  const song = useMemo(() => findArcadeSong(lineup, songSlug), [lineup, songSlug]);
  const artistName = artist?.name || ARCADE_ARTIST;
  const artistImage = artist?.images?.[0]?.url;

  // Coming from a song card, the card's cover and title are painted on the
  // first commit and the card grows into the stage; the real stage mounts once
  // that morph has finished.
  const handoff = useSharedArrival();
  const holdForTransition = useHoldForArrival(handoff);

  const [prefs, setPrefs] = useState<ArcadePrefs>(() => readArcadePrefs());
  const [linksOpen, setLinksOpen] = useState(false);
  const updatePrefs = useCallback((next: Partial<ArcadePrefs>) => {
    setPrefs(() => writeArcadePrefs(next));
  }, []);

  const theme = useSongTheme(song, prefs.palette);
  // Old links carried the game as ?game=; each game now has its own path.
  const legacyGame = searchParams.get("game");
  const requestedGame = gameKind ?? legacyGame;
  const game: ArcadeGameKind | null = theme
    ? isArcadeGameKind(requestedGame) && theme.games.includes(requestedGame)
      ? requestedGame
      : theme.featuredGame
    : null;

  // ?challenge=YYYY-MM-DD counts only while that day's challenge is open and
  // this page is its song and game; anything else is a plain game.
  const challengeParam = searchParams.get(CHALLENGE_PARAM);
  const challenge = useMemo(() => {
    if (!song || !game || !challengeParam || !isOpenChallengeDate(challengeParam, Date.now())) return null;
    const pick = dailyChallengeFor(lineup, challengeParam, (entry, kind) => arcadeThemeFor(entry).games.includes(kind));
    return pick && pick.song.slug === song.slug && pick.game === game ? challengeParam : null;
  }, [challengeParam, game, lineup, song]);
  const retention = useArcadeRetention({ game: game ?? "beat-lanes", songSlug: song?.slug ?? "", songTitle: song?.title ?? "", challenge });

  const { track, state: trackState } = useArcadeTrack(song, artistName);
  const { currentTrack, isPlaying, addToPlaylist, togglePlayPause, setPlaybackMode } = usePlaylist();
  const waitingForTrack = useRef(false);
  useEffect(() => {
    waitingForTrack.current = false;
    return () => setPlaybackMode("normal");
  }, [songSlug, setPlaybackMode]);

  const songIsCurrent = Boolean(song && currentTrack && sameSong(currentTrack, song));
  const songIsPlaying = songIsCurrent && isPlaying;

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }, [songSlug]);

  const playSong = useCallback(() => {
    setPlaybackMode("repeat-track");
    if (!track) { waitingForTrack.current = true; return; }
    waitingForTrack.current = false;
    if (songIsCurrent) {
      if (!isPlaying) togglePlayPause();
      return;
    }
    addToPlaylist(track);
  }, [track, songIsCurrent, isPlaying, togglePlayPause, addToPlaylist, setPlaybackMode]);

  useEffect(() => {
    if (waitingForTrack.current && track) playSong();
  }, [track, playSong]);

  const onStart = useCallback(() => {
    if (prefs.music) playSong();
  }, [prefs.music, playSong]);

  const toggleSong = () => {
    if (songIsPlaying) {
      trackClick("arcade:pause-song", { label: "Arcade pause song", section: "arcade", track: song?.title });
      togglePlayPause();
    } else {
      trackClick("arcade:play-song", { label: "Arcade play song", section: "arcade", track: song?.title });
      playSong();
    }
  };

  const pickGame = (kind: ArcadeGameKind) => {
    trackClick(`arcade:pick:${kind}`, { label: `Arcade pick ${ARCADE_GAMES[kind].name}`, section: "arcade", track: song?.title });
  };

  const moreSongs = useMemo(() => {
    if (!song) return [];
    const index = lineup.findIndex((entry) => entry.slug === song.slug);
    const rotated = [...lineup.slice(index + 1), ...lineup.slice(0, Math.max(0, index))];
    return rotated.slice(0, MORE_SONGS);
  }, [lineup, song]);

  const pageShell = (content: React.ReactNode) => (
    <Layout artwork={artistImage}>
      <ArtistNav artist={artistName} artistImage={artistImage} />
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          backgroundColor: "var(--backgroundColor)",
          pt: { xs: "12rem", md: "10rem" },
          pb: { xs: "4rem", md: "6rem" },
          px: { xs: "2.5rem", md: "5rem" },
        }}
      >
        <Box sx={{ maxWidth: "1280px", margin: "0 auto" }}>{content}</Box>
      </Box>
      <Footer artistData={artist} />
    </Layout>
  );

  const backLink = (
    <Box
      component={RouterLink}
      to={arcadePath()}
      sx={{ display: "inline-flex", alignItems: "center", gap: "6px", minHeight: "44px", color: "var(--logoColor)", textDecoration: "none", fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, fontWeight: 600, "&:hover": { color: "var(--logoColor2)" } }}
    >
      <ArrowBackIcon sx={{ fontSize: "18px" }} /> All games
    </Box>
  );

  if (song && legacyGame !== null) {
    const rest = new URLSearchParams(searchParams);
    rest.delete("game");
    const query = rest.toString();
    const kind = isArcadeGameKind(legacyGame) ? legacyGame : undefined;
    return <Navigate to={`${arcadePath(song.slug, kind)}${query ? `?${query}` : ""}`} replace />;
  }
  if (song && theme && gameKind && !(isArcadeGameKind(gameKind) && theme.games.includes(gameKind))) {
    return <Navigate to={arcadePath(song.slug)} replace />;
  }
  if (handoff && (holdForTransition || (loading && (!song || !theme || !game)))) {
    return pageShell(
      <Box aria-busy>
        <SEO title={handoff.title ? `${handoff.title} Game: Play Free` : "Loading the arcade..."} />
        {backLink}
        <GameHeader artwork={song?.artwork || handoff.artwork} title={song?.title || handoff.title || ""} eyebrow={"\u00a0"} />
        <Box aria-hidden sx={{ ...pickerRowSx, gridTemplateColumns: { md: `repeat(${theme?.games.length ?? ARCADE_GAME_KINDS.length}, minmax(0, 1fr))` } }}>
          {(theme?.games ?? ARCADE_GAME_KINDS).map((kind) => (
            <Box key={kind} sx={{ aspectRatio: "4 / 3", borderRadius: "14px", border: "2px solid transparent", backgroundColor: "color-mix(in srgb, var(--logoColor) 10%, transparent)" }} />
          ))}
        </Box>
        <Box
          {...heroProps("stage")}
          sx={{ height: STAGE_HEIGHT, minHeight: "380px", borderRadius: { xs: "18px", md: "22px" }, backgroundColor: theme?.palette.backgroundDeep ?? "color-mix(in srgb, var(--logoColor) 10%, transparent)" }}
        />
      </Box>,
    );
  }

  if (!song || !theme || !game) {
    if (loading) {
      return pageShell(
        <Box aria-busy sx={{ display: "grid", gap: "18px" }}>
          <SEO title="Loading the arcade..." />
          {backLink}
          <Box sx={{ height: { xs: "min(74vh, 150vw)", md: "min(66vh, 640px)" }, borderRadius: "22px", backgroundColor: "color-mix(in srgb, var(--logoColor) 10%, transparent)" }} />
        </Box>,
      );
    }
    return pageShell(
      <Box sx={{ display: "grid", gap: "16px", justifyItems: "start" }}>
        <SEO title="Game not found" description="That song is not in the arcade." noindex />
        {backLink}
        <Typography component="h1" sx={{ fontFamily: "Hanson", fontWeight: 700, color: "var(--logoColor2)", fontSize: { xs: "3rem", md: "2.5rem" }, m: 0 }}>
          No game here
        </Typography>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", color: "var(--logoColor)", fontSize: { xs: "17px", md: "16px" } }}>
          That song is not in the arcade. Pick another one from the lineup.
        </Typography>
      </Box>,
    );
  }

  const info = ARCADE_GAMES[game];
  const palette = theme.palette;
  const releasePath = getArtistPath(artistName, (song.release.totalTracks >= 3 ? "/albums/" : "/songs/") + safeStringWithHyphens(song.release.name));
  const year = releaseYear(song);

  return pageShell(
    <>
      {gameKind ? (
        <SEO
          title={gameTitle(song.title, game)}
          description={gameDescription(song.title, artistName, game)}
          image={song.artwork}
          url={arcadePath(song.slug, game)}
          type="website"
          keywords={`${song.title} ${info.name}, ${song.title} ${info.genre.toLowerCase()} game, ${artistName} games, free music game`}
        />
      ) : (
        <SEO
          title={`${song.title} Game: Play Free`}
          description={`Play ${song.title} by ${artistName} as ${info.name} or ${theme.games.length - 1} other arcade games that move to the beat: racing, rhythm and shooters dressed in the song's cover art.`}
          image={song.artwork}
          url={arcadePath(song.slug)}
          type="website"
          keywords={`${song.title} game, ${artistName} games, music game, rhythm game`}
        />
      )}
      {backLink}

      <GameHeader
        artwork={song.artwork}
        title={song.title}
        eyebrow={`${badgeFor(song)} · ${RELEASE_KIND_LABELS[song.release.kind].one}${year ? ` · ${year}` : ""}`}
        eyebrowColor={palette.primary}
        glow={palette.primary}
        releasePath={releasePath}
      />

      <Box
        role="group"
        aria-label="Pick a game"
        sx={{ ...pickerRowSx, gridTemplateColumns: { md: `repeat(${theme.games.length}, minmax(0, 1fr))` } }}
      >
        {theme.games.map((kind) => {
          const selected = kind === game;
          return (
            <ButtonBase
              key={kind}
              component={RouterLink}
              to={arcadePath(song.slug, kind)}
              replace
              preventScrollReset
              onClick={() => pickGame(kind)}
              aria-current={selected ? "page" : undefined}
              aria-label={`${ARCADE_GAMES[kind].name}, ${ARCADE_GAMES[kind].genre}${kind === theme.featuredGame ? ", made for this song" : ""}`}
              sx={{
                position: "relative",
                display: "block",
                aspectRatio: "4 / 3",
                borderRadius: "14px",
                overflow: "hidden",
                scrollSnapAlign: "start",
                border: `2px solid ${selected ? palette.primary : "transparent"}`,
                boxShadow: selected ? `0 0 0 3px color-mix(in srgb, ${palette.primary} 25%, transparent), 0 14px 30px -14px ${palette.primary}` : "none",
                transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
                "&:focus-visible": { outline: `2px solid ${palette.accent}`, outlineOffset: "2px" },
              }}
            >
              <GameThumbnail kind={kind} theme={theme} coverUrl={song.artwork} width={240} height={180} eager />
              <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85))" }} />
              <Box sx={{ position: "absolute", left: "10px", right: "10px", bottom: "8px", textAlign: "left" }}>
                <Typography component="span" sx={{ display: "block", fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: kind === theme.featuredGame ? palette.accent : "rgba(255,255,255,0.75)" }}>
                  {kind === theme.featuredGame ? "Made for this song" : ARCADE_GAMES[kind].genre}
                </Typography>
                <Typography component="span" sx={{ display: "block", fontFamily: "Hanson", fontWeight: 700, fontSize: { xs: "13px", md: "15px" }, color: "#fff", lineHeight: 1.15 }}>
                  {ARCADE_GAMES[kind].name}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {challenge ? (
        <Box
          role="note"
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 12px", mb: "12px", p: "10px 16px", borderRadius: "14px", backgroundColor: `color-mix(in srgb, ${palette.primary} 16%, transparent)`, border: `1px solid color-mix(in srgb, ${palette.primary} 40%, transparent)` }}
        >
          <Typography component="span" sx={{ ...labelSx, color: palette.accent, opacity: 1 }}>Daily challenge</Typography>
          <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, color: "var(--logoColor2)" }}>
            Scores you post here also go on today's challenge board.
          </Typography>
          <Typography component="span" sx={{ fontFamily: "Poppins, sans-serif", fontSize: { xs: "14px", md: "13px" }, color: "var(--logoColor)", fontVariantNumeric: "tabular-nums", ml: { md: "auto" } }}>
            <ChallengeCountdown prefix="Ends in" />
          </Typography>
        </Box>
      ) : null}

      <Box {...heroProps("stage")}>
      <ArcadeStage
        game={game}
        theme={theme}
        songSlug={song.slug}
        song={{ title: song.title, artwork: song.artwork }}
        difficulty={prefs.difficulty}
        onStart={onStart}
        onSongClick={() => setLinksOpen(true)}
        retention={retention}
      />
      </Box>
      <SongLinksSheet
        open={linksOpen}
        onClose={() => setLinksOpen(false)}
        song={{
          title: song.title,
          artist: artistName,
          artwork: song.artwork,
          meta: `${RELEASE_KIND_LABELS[song.release.kind].one}${year ? ` · ${year}` : ""}`,
          spotifyTrackId: song.trackId,
          spotifyAlbumId: song.release.id,
          albumName: song.release.name,
          releasePath,
        }}
      />

      <Typography sx={{ mt: "12px", fontFamily: "Poppins, sans-serif", fontSize: { xs: "14px", md: "14px" }, color: "var(--logoColor)", lineHeight: 1.5 }}>
        {info.blurb}
      </Typography>

      <Box sx={{ mt: { xs: "20px", md: "24px" }, display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: { xs: "18px", md: "28px" } }}>
        <Segmented
          label="Difficulty"
          options={ARCADE_DIFFICULTIES}
          value={prefs.difficulty}
          labels={DIFFICULTY_LABELS}
          onChange={(difficulty: ArcadeDifficulty) => updatePrefs({ difficulty })}
          accent={palette.primary}
          contrastText={palette.backgroundDeep}
        />
        <Segmented
          label="Colours"
          options={PALETTE_CHOICES}
          value={prefs.palette}
          labels={PALETTE_LABELS}
          onChange={(palette: PaletteChoice) => updatePrefs({ palette })}
          accent={palette.primary}
          contrastText={palette.backgroundDeep}
        />
        <Box sx={{ display: "grid", gap: "8px" }}>
          <Typography component="span" sx={labelSx}>Music</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <ButtonBase
              onClick={toggleSong}
              disabled={!track}
              sx={{
                minHeight: "48px",
                px: "18px",
                gap: "8px",
                borderRadius: "999px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: 700,
                color: "var(--color5)",
                backgroundColor: "var(--logoColor2)",
                opacity: track ? 1 : 0.5,
                "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
              }}
            >
              {songIsPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
              {trackState === "loading" ? "Finding the song..." : trackState === "unavailable" ? "No preview for this song" : songIsPlaying ? "Pause the song" : "Play the song"}
            </ButtonBase>
            <ButtonBase
              onClick={() => updatePrefs({ music: !prefs.music })}
              aria-pressed={prefs.music}
              sx={{
                minHeight: "48px",
                px: "16px",
                gap: "8px",
                borderRadius: "999px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "var(--logoColor2)",
                border: "1px solid color-mix(in srgb, var(--logoColor) 30%, transparent)",
                "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
              }}
            >
              {prefs.music ? <MusicNoteIcon sx={{ fontSize: 20 }} /> : <MusicOffIcon sx={{ fontSize: 20 }} />}
              {prefs.music ? "Plays when you start" : "Start without music"}
            </ButtonBase>
          </Box>
        </Box>
      </Box>

      {moreSongs.length > 0 ? (
        <Box component="section" aria-labelledby="arcade-more" sx={{ mt: { xs: "3.5rem", md: "4.5rem" } }}>
          <Typography id="arcade-more" component="h2" sx={{ m: "0 0 16px", fontFamily: "Hanson", fontWeight: 700, color: "var(--logoColor2)", fontSize: { xs: "2rem", md: "1.5rem" } }}>
            PLAY ANOTHER SONG
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" }, gap: { xs: "14px", md: "20px" } }}>
            {moreSongs.map((entry) => (
              <ArcadeSongCard key={entry.slug} song={entry} clickTarget="arcade:next-song" />
            ))}
          </Box>
        </Box>
      ) : null}
    </>,
  );
}
